// components/whatsapp/WhatsAppIntakeSheet.tsx
'use client';

/**
 * Asistente de fechas antes de WhatsApp — handoff W2 + visión founder 2026-06-10.
 *
 * Copy facilitador ("te respondemos mucho más rápido"), pide entrada/salida/
 * huéspedes y según `mode`:
 *  - 'message'  → construye mensaje natural y abre WhatsApp (v1, Etapa 1)
 *  - 'results'  → redirige a /reservas con la búsqueda ejecutada para que el
 *                 cliente VEA resultados reales antes de escribir (v2, Etapa 2)
 * NUNCA es barrera: el escape "Preguntar sin fechas" siempre visible.
 *
 * Variantes (context-aware 2026-06-11):
 *  - 'stay' (default): check-in/check-out + huéspedes con niños (mismo
 *    GuestPickerPopover del buscador — adultos + niños con edades; los niños
 *    5+ ocupan cupo de adulto para capacidad, igual que AvailabilitySearch).
 *  - 'tour': fecha única del día del tour + personas → mensaje directo a
 *    WhatsApp. En páginas de tours preguntar check-in/check-out era
 *    incoherente con lo que el cliente está mirando.
 *
 * - PORTAL a document.body: el sheet vivía dentro de Header (backdrop-filter)
 *   y BuildingHero (transform/parallax) — esos ancestros crean containing block
 *   y rompen `position: fixed` (modal pegado arriba/cortado en desktop).
 *   Mismo patrón que DatePickerPopover.
 * - Fechas con el calendario optimizado del sitio (DatePickerPopover →
 *   ModernDatePicker), no inputs nativos — consistencia visual (founder 2026-06-10).
 * - Mobile: bottom sheet. Desktop: card centrada con overlay suave.
 * - cta_click_whatsapp se emite SOLO al salir hacia WhatsApp (continuar en
 *   modo message, o escape), nunca al abrir el sheet (eso es wsp_intake_opened).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import * as Popover from '@radix-ui/react-popover';
import { DayPicker } from 'react-day-picker';
import { es, enUS } from 'date-fns/locale';
import 'react-day-picker/style.css';
import { CalendarDays, Users } from 'lucide-react';
import DatePickerPopover from '@/components/shared/DatePickerPopover';
import GuestPickerPopover from '@/components/shared/GuestPickerPopover';
import WhatsAppGlyph from '@/components/whatsapp/WhatsAppGlyph';
import UrgentDepartureNotice from '@/components/tours/UrgentDepartureNotice';
import { useLanguage } from '@/components/tours/LanguageProvider';
import { addDaysLocalISO, calculateDeparture, getDateInTimeZoneISO } from '@/lib/dateUtils';
import { resolveBookingLeadDays, resolveLiveTourContactMinDate } from '@/lib/tours/booking-lead-time';
import { isUrgentTourDate } from '@/lib/tours/urgent-departure';
import { quickRanges, tourQuickDates } from '@/lib/whatsapp/intake-dates';
import { loadIntakeStayState, saveIntakeStayState } from '@/lib/whatsapp/intake-state';
import { trackPostHogEvent } from '@/lib/analytics/posthog';
import {
  fireWspReservasConversion,
  fireWspToursConversion,
} from '@/lib/analytics/gtag-conversion';
import {
  buildQuoteMessage,
  buildApartmentMessage,
  buildTourMessage,
  buildEscapeMessage,
} from '@/lib/whatsapp/qualified-message';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import type { WspEntryContext } from '@/lib/analytics/wsp-qualification';

interface ChildAge {
  id: string;
  age: '4-' | '5+';
}

export interface WhatsAppIntakeContext {
  /** Posición del trigger, ej. 'intake_header', 'intake_building_hero'. */
  ctaPosition: string;
  entryContext: WspEntryContext;
  /** 'stay' (default): check-in/check-out de apartamentos.
   *  'tour': fecha única del tour + personas (mensaje directo a WhatsApp). */
  variant?: 'stay' | 'tour';
  buildingSlug?: string;
  buildingName?: string;
  neighborhood?: string;
  apartmentId?: string;
  apartmentName?: string;
  tourSlug?: string;
  tourName?: string;
  /** Primera fecha operativamente válida para una consulta de este tour. */
  tourMinDateISO?: string;
  /** Hora pública del tour para recalcular el cutoff al confirmar, no al abrir. */
  tourStartTime?: string;
  tourBookingLeadDays?: number;
  source?: string;
  /** 'results' (default Etapa 2): redirige a /reservas con la búsqueda hecha.
   *  'message': va directo a WhatsApp con mensaje natural (fichas concretas). */
  mode?: 'results' | 'message';
}

interface Props {
  open: boolean;
  onClose: () => void;
  context: WhatsAppIntakeContext;
}

function formatDiaCorto(iso: string, english = false): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(english ? 'en-US' : 'es-CO', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function formatRangoCorto(arrival: string, departure: string): string {
  return `${formatDiaCorto(arrival)} → ${formatDiaCorto(departure)}`;
}

// Re-export de compatibilidad: tests y consumidores históricos los importaban
// de aquí. La fuente vive en lib/whatsapp/intake-dates (módulo puro sin CSS).
export { quickRanges, tourQuickDates };

export default function WhatsAppIntakeSheet({ open, onClose, context }: Props) {
  const router = useRouter();
  const variant = context.variant ?? 'stay';
  // La ruta /en/ fija el idioma aunque la preferencia guardada sea ES.
  // Compartir el contexto evita cambiar de idioma al abrir el asistente.
  const { locale } = useLanguage();
  const english = variant === 'tour' && locale === 'en';
  const tr = (spanish: string, en: string) => english ? en : spanish;

  // --- Estado variante 'stay' (check-in/check-out + huéspedes con niños) ---
  const [arrival, setArrival] = useState('');
  const [nights, setNights] = useState(2);
  const [numAdults, setNumAdults] = useState(2);
  const [childrenAges, setChildrenAges] = useState<ChildAge[]>([]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [guestPickerOpen, setGuestPickerOpen] = useState(false);

  // --- Estado variante 'tour' (fecha única + personas) ---
  const [tourDate, setTourDate] = useState('');
  const [people, setPeople] = useState(2);
  const [tourCalOpen, setTourCalOpen] = useState(false);

  const dateTriggerRef = useRef<HTMLButtonElement>(null);
  const guestTriggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const departure = arrival ? calculateDeparture(arrival, nights) : '';
  const [clockMs, setClockMs] = useState(() => Date.now());
  useEffect(() => {
    if (!open || variant !== 'tour') return;
    setClockMs(Date.now());
    const interval = window.setInterval(() => setClockMs(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, [open, variant]);
  const tourMinDateISO = resolveLiveTourContactMinDate({
    startTime: context.tourStartTime,
    bookingLeadDays: context.tourBookingLeadDays,
    fallbackISO: context.tourMinDateISO,
    now: new Date(clockMs),
  });
  // Salida urgente: fecha en hora de Bogotá (no la del navegador), con hora de
  // salida conocida y dentro de la anticipación del pago online.
  const urgentCheck = (date: string) =>
    variant === 'tour' && Boolean(date) && isUrgentTourDate({
      tourDate: date,
      startTime: context.tourStartTime,
      bookingLeadDays: resolveBookingLeadDays(context.tourBookingLeadDays),
      now: new Date(clockMs),
    });
  const bogotaTodayISO = getDateInTimeZoneISO(new Date(clockMs));
  const urgentTodayISO = urgentCheck(bogotaTodayISO) ? bogotaTodayISO : undefined;
  const isUrgentPick = urgentCheck(tourDate);

  // Convención del buscador (AvailabilitySearch): los niños 5+ ocupan cupo de
  // adulto para capacidad; los 0-4 no. El mensaje humano usa el total real.
  const numChildren = childrenAges.length;
  const children5Plus = childrenAges.filter((c) => c.age === '5+').length;
  const totalPeople = numAdults + numChildren;
  const totalAdults = numAdults + children5Plus;

  // Portal root al body (cliente) — mismo patrón que DatePickerPopover.
  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  // Avisar a widgets globales (Lía z-10000) que el sheet está abierto para
  // que se oculten. El evento solo llega a listeners YA montados; con
  // ?intent=wsp el sheet abre durante el montaje inicial y Lía (lazy) puede
  // registrarse después — por eso TAMBIÉN se espeja en un dataset del body
  // que Lía consulta al montar (auditoría 2026-06-10). Solo se notifica en
  // transiciones reales: hay múltiples instancias del sheet montadas (Header,
  // Footer, etc.) y un dispatch de `false` al montar pisaría a la abierta.
  useEffect(() => {
    const notify = (value: boolean) => {
      if (value) {
        document.body.dataset.wspIntakeOpen = 'true';
      } else {
        delete document.body.dataset.wspIntakeOpen;
      }
      window.dispatchEvent(new CustomEvent('wsp-intake-state', { detail: { open: value } }));
    };
    if (open) {
      notify(true);
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      notify(false);
      wasOpenRef.current = false;
    }
    return () => {
      if (wasOpenRef.current) {
        notify(false);
        wasOpenRef.current = false;
      }
    };
  }, [open]);

  // Accesibilidad mínima del modal: Escape cierra (primero los popovers
  // internos si están abiertos), el body no scrollea detrás y el foco entra al
  // panel (y vuelve al trigger al cerrar). El focus trap completo queda para
  // la migración a Radix Dialog.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (datePickerOpen) {
        setDatePickerOpen(false);
        return;
      }
      if (guestPickerOpen) {
        setGuestPickerOpen(false);
        return;
      }
      if (tourCalOpen) {
        setTourCalOpen(false);
        return;
      }
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, datePickerOpen, guestPickerOpen, tourCalOpen]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const prevFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (variant === 'tour' && tourDate && tourDate < tourMinDateISO) {
      setTourDate('');
    }
  }, [variant, tourDate, tourMinDateISO]);

  useEffect(() => {
    if (!open) return;
    trackPostHogEvent('wsp_intake_opened', {
      cta_position: context.ctaPosition,
      entry_context: context.entryContext,
      building_slug: context.buildingSlug,
      apartment_id: context.apartmentId,
      tour_slug: context.tourSlug,
      source: context.source,
      intake_mode: context.mode ?? 'results',
      intake_variant: variant,
    });
    // Solo `open` como dep: `context` llega como objeto inline de los callers
    // y cambiaría de identidad en cada render del padre, re-disparando el
    // evento con el sheet abierto. Un open = un wsp_intake_opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Paso intermedio del embudo por sesión (A4): primera fecha fijada en esta
  // apertura, en cualquiera de las dos variantes. Con estado stay hidratado de
  // una apertura anterior dispara igual: es la misma sesión y el análisis del
  // embudo es por sesión, no por apertura.
  const dateSelectedSentRef = useRef(false);
  useEffect(() => {
    if (!open) {
      dateSelectedSentRef.current = false;
      return;
    }
    const hasDate = variant === 'tour' ? Boolean(tourDate) : Boolean(arrival);
    if (hasDate && !dateSelectedSentRef.current) {
      dateSelectedSentRef.current = true;
      trackPostHogEvent('wsp_intake_date_selected', {
        cta_position: context.ctaPosition,
        entry_context: context.entryContext,
        intake_variant: variant,
        tour_slug: context.tourSlug,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tourDate, arrival, variant]);

  // --- Estado compartido entre instancias del sheet (hero, sticky, header…) ---
  // Cada instancia tenía su propio useState: el cliente elegía fechas en un
  // botón y al enviar desde otro las perdía → mensaje genérico → la conversión
  // calificada de Ads no disparaba. Hidratar al abrir y persistir al cambiar
  // hace que la selección viaje entre todos los botones. Solo variante 'stay'
  // (la fecha de tour es específica de la página, no se comparte).
  useEffect(() => {
    if (!open || variant === 'tour') return;
    const saved = loadIntakeStayState();
    if (!saved) return;
    setArrival(saved.arrival);
    setNights(saved.nights);
    setNumAdults(saved.numAdults);
    setChildrenAges(saved.childrenAges);
    // Solo al abrir: hidratar una vez la última selección del cliente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (variant === 'tour') return;
    if (!arrival) return; // sin fecha no hay nada que compartir
    saveIntakeStayState({ arrival, nights, numAdults, childrenAges });
  }, [variant, arrival, nights, numAdults, childrenAges]);

  const canContinue =
    variant === 'tour'
      ? Boolean(tourDate && tourDate >= tourMinDateISO && people > 0)
      : Boolean(arrival && departure && departure > arrival && totalPeople > 0);

  const handleContinue = useCallback(() => {
    if (!canContinue) return;

    // --- Variante tour: siempre mensaje directo a WhatsApp ---
    if (variant === 'tour') {
      const liveMin = resolveLiveTourContactMinDate({
        startTime: context.tourStartTime,
        bookingLeadDays: context.tourBookingLeadDays,
        fallbackISO: context.tourMinDateISO,
      });
      if (!tourDate || tourDate < liveMin) {
        setTourDate('');
        return;
      }
      trackPostHogEvent('wsp_intake_completed', {
        cta_position: context.ctaPosition,
        entry_context: context.entryContext,
        intake_mode: 'message',
        intake_variant: 'tour',
        tour_slug: context.tourSlug,
        tour_date: tourDate,
        guests_total: people,
      });
      openTrackedWhatsApp(english ? `${isUrgentPick ? 'URGENT departure. ' : ''}Hi! I'd like to check availability for ${context.tourName ?? 'a tour'}.\nDate: ${tourDate}\nPeople: ${people}` : buildTourMessage({ date: tourDate, people, tourName: context.tourName, urgent: isUrgentPick }), {
        source: context.source ?? 'intake',
        cta_position: context.ctaPosition,
        entry_context: context.entryContext,
        // Convención tours (TourBookingBox): arrival = departure = día del tour.
        arrival: tourDate,
        departure: tourDate,
        guests_total: people,
        num_adults: people,
        tour_slug: context.tourSlug,
      });
      fireWspToursConversion({
        cta_position: context.ctaPosition,
        tour_slug: context.tourSlug,
        tour_date: tourDate,
        guests: people,
      });
      onClose();
      return;
    }

    trackPostHogEvent('wsp_intake_completed', {
      cta_position: context.ctaPosition,
      entry_context: context.entryContext,
      arrival, departure,
      guests_total: totalPeople,
      num_adults: numAdults,
      num_children: numChildren,
      building_slug: context.buildingSlug,
      apartment_id: context.apartmentId,
      intake_mode: context.mode ?? 'results',
      intake_variant: 'stay',
    });

    // Etapa 2 — modo results: el cliente ve disponibilidad real ANTES de
    // escribir. /reservas ya lee estos params y ejecuta la búsqueda; la franja
    // y el sticky de WhatsApp calificado (Etapa 1) lo esperan allá.
    // numAdults lleva la convención de capacidad del buscador (totalAdults).
    if ((context.mode ?? 'results') === 'results' && !context.apartmentName) {
      const params = new URLSearchParams({
        arrival,
        departure,
        numAdults: String(totalAdults),
        guestAdults: String(numAdults),
        source: context.source ?? 'intake_wsp',
      });
      if (childrenAges.length > 0) {
        params.set(
          'childrenAges',
          childrenAges.map((child) => child.age).join(',')
        );
      }
      if (context.buildingSlug) params.set('building', context.buildingSlug);
      if (context.neighborhood) params.set('neighborhood', context.neighborhood);
      onClose();
      router.push(`/reservas?${params.toString()}`);
      return;
    }

    // Modo message: mensaje natural directo a WhatsApp (fichas concretas).
    const guestBreakdown = { guests: totalPeople, numAdults, numChildren };
    const message = context.apartmentName
      ? buildApartmentMessage({
          arrival, departure, ...guestBreakdown,
          apartmentName: context.apartmentName,
        })
      : buildQuoteMessage({
          arrival, departure, ...guestBreakdown,
          buildingName: context.buildingName,
          neighborhood: context.neighborhood,
        });

    openTrackedWhatsApp(message, {
      source: context.source ?? 'intake',
      cta_position: context.ctaPosition,
      entry_context: context.entryContext,
      arrival, departure,
      guests_total: totalPeople,
      num_adults: totalAdults,
      small_children: numChildren - children5Plus,
      apartment_id: context.apartmentId,
      apartment_name: context.apartmentName,
      building_id: context.buildingSlug,
    });
    fireWspReservasConversion({
      cta_position: context.ctaPosition,
      arrival, departure,
      num_adults: totalAdults,
      apartment_id: context.apartmentId,
    });
    onClose();
  }, [
    canContinue, variant, arrival, departure, tourDate, people,
    totalPeople, totalAdults, numAdults, numChildren, children5Plus,
    context, onClose, router, english, isUrgentPick,
  ]);

  const handleBypass = useCallback(() => {
    trackPostHogEvent('wsp_click_without_dates_bypass', {
      cta_position: context.ctaPosition,
      entry_context: context.entryContext,
      intake_variant: variant,
    });
    const escapeMessage = english ? `Hi! I'd like information about ${context.tourName ?? 'a tour'}. I haven't chosen a date yet.` : buildEscapeMessage({
      variant,
      apartmentName: context.apartmentName,
      buildingName: context.buildingName,
      neighborhood: context.neighborhood,
      tourName: context.tourName,
    });
    openTrackedWhatsApp(escapeMessage, {
      source: context.source ?? 'intake',
      cta_position: context.ctaPosition,
      entry_context: context.entryContext,
      tour_slug: context.tourSlug,
    });
    onClose();
  }, [context, variant, onClose, english]);

  if (!open || !portalRoot) return null;

  const isResultsMode =
    variant === 'stay' && (context.mode ?? 'results') === 'results' && !context.apartmentName;

  const guestsLabel = `${numAdults} ${numAdults === 1 ? 'adulto' : 'adultos'}${
    numChildren > 0 ? ` · ${numChildren} ${numChildren === 1 ? 'niño' : 'niños'}` : ''
  }`;

  return createPortal(
    // `pointer-events-auto` es obligatorio: cuando el sheet abre sobre un Dialog
    // modal de Radix (MobileBookingDrawer en tours móvil), Radix deja <body> con
    // `pointer-events:none` y este portal a body heredaba `none` → ningún control
    // respondía y el cliente hacía rageclick sobre "2 personas" (replay PostHog
    // 01a02af2… 2026-08-22). Radix NO lo cierra por "clic fuera" porque el sheet
    // sigue dentro de su árbol React. Gate: tests/e2e/test-mobile-wsp-intake-over-drawer.ts
    <div
      className="pointer-events-auto fixed inset-0 z-[70] flex items-end md:items-center md:justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wsp-intake-title"
    >
      {/* Overlay suave — click cierra (no es bloqueo) */}
      <button
        type="button"
        aria-label={tr('Cerrar', 'Close')}
        onClick={onClose}
        className="absolute inset-0 bg-gray-950/40 backdrop-blur-[2px] animate-in fade-in duration-200"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full md:max-w-md rounded-t-3xl md:rounded-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] md:pb-6 shadow-xl outline-none animate-in slide-in-from-bottom-4 md:slide-in-from-bottom-2 fade-in duration-200"
      >
        <h2 id="wsp-intake-title" className="text-lg font-semibold text-gray-900">
          {variant === 'tour' ? tr('Elige el día de tu tour', 'Choose your tour date') : 'Consulta tus fechas primero'}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {variant === 'tour'
            ? context.tourName
              ? tr(`Así te confirmamos cupos y precio de ${context.tourName} mucho más rápido por WhatsApp.`, `Check availability and pricing for ${context.tourName} on WhatsApp.`)
              : tr('Así te confirmamos cupos y precios reales mucho más rápido por WhatsApp.', 'Check availability and current prices on WhatsApp.')
            : 'Así te respondemos mucho más rápido por WhatsApp, con disponibilidad y precio reales.'}
        </p>

        {variant === 'tour' ? (
          <>
            {/* Fechas rápidas del tour — caso común a 1 tap */}
            <div className="mt-3 flex flex-wrap gap-2">
              {tourQuickDates(new Date(), tourMinDateISO, urgentTodayISO).map((d) => (
                <button
                  key={english ? ({'Hoy':'Today','Mañana':'Tomorrow','Este sábado':'This Saturday','Este domingo':'This Sunday'} as Record<string,string>)[d.label] ?? d.label : d.label}
                  type="button"
                  onClick={() => setTourDate(d.date)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    tourDate === d.date
                      ? 'border-emerald-700 bg-emerald-50 text-emerald-800'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {english ? ({'Hoy':'Today','Mañana':'Tomorrow','Este sábado':'This Saturday','Este domingo':'This Sunday'} as Record<string,string>)[d.label] ?? d.label : d.label}
                </button>
              ))}
            </div>

            {/* Día del tour — calendario de fecha única (patrón TourDatePicker) */}
            <Popover.Root open={tourCalOpen} onOpenChange={setTourCalOpen}>
              <Popover.Trigger asChild>
                <button
                  type="button"
                  className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-gray-300 px-4 py-3 text-left transition-colors hover:border-gray-400"
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <CalendarDays className="h-4.5 w-4.5 shrink-0 text-gray-500" strokeWidth={1.75} />
                    {tourDate ? (
                      <span className="truncate text-sm font-medium text-gray-900">
                        {formatDiaCorto(tourDate, english)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">{tr('Elige el día', 'Choose a date')}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs font-semibold text-emerald-700">
                    {tourDate ? tr('Cambiar', 'Change') : tr('Abrir calendario', 'Open calendar')}
                  </span>
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  align="start"
                  sideOffset={8}
                  className="z-[80] w-[312px] rounded-2xl border border-gray-200 bg-white p-3 shadow-[0_8px_30px_rgba(0,0,0,0.12)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
                >
                  <DayPicker
                    mode="single"
                    selected={tourDate ? new Date(tourDate + 'T12:00:00') : undefined}
                    onSelect={(d) => {
                      if (!d) return;
                      // Serialización local — toISOString correría la fecha tras las 19:00 en Bogotá.
                      setTourDate(addDaysLocalISO(d, 0));
                      setTourCalOpen(false);
                    }}
                    locale={english ? enUS : es}
                    showOutsideDays={false}
                    disabled={{ before: new Date(tourMinDateISO + 'T12:00:00') }}
                    modifiersClassNames={{ selected: 'rdp-selected-day' }}
                  />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>

            {/* Personas — contador simple: en tours los niños pagan según la
                ficha de cada tour; el desglose se acuerda por WhatsApp. */}
            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-gray-300 px-4 py-2.5">
              <span className="text-sm text-gray-700">{tr('Personas', 'People')}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPeople((g) => Math.max(1, g - 1))}
                  className="h-9 w-9 rounded-lg border border-gray-300 text-lg font-semibold text-gray-700 transition-colors hover:border-gray-400 disabled:opacity-40"
                  disabled={people <= 1}
                  aria-label={tr('Menos personas', 'Fewer people')}
                >
                  −
                </button>
                <span className="min-w-[6.5rem] text-center text-sm font-medium text-gray-900">
                  {people} {people === 1 ? tr('persona', 'person') : tr('personas', 'people')}
                </span>
                <button
                  type="button"
                  onClick={() => setPeople((g) => Math.min(30, g + 1))}
                  className="h-9 w-9 rounded-lg border border-gray-300 text-lg font-semibold text-gray-700 transition-colors hover:border-gray-400 disabled:opacity-40"
                  disabled={people >= 30}
                  aria-label={tr('Más personas', 'More people')}
                >
                  +
                </button>
              </div>
            </div>
            {isUrgentPick && (
              <div className="mt-3">
                <UrgentDepartureNotice
                  tourDate={tourDate}
                  startTime={context.tourStartTime}
                  english={english}
                  onExpire={() => setTourDate('')}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Rangos rápidos — caso común a 1 tap */}
            <div className="mt-3 flex flex-wrap gap-2">
              {quickRanges().map((r) => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => {
                    setArrival(r.arrival);
                    setNights(r.nights);
                  }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    arrival === r.arrival && nights === r.nights
                      ? 'border-emerald-700 bg-emerald-50 text-emerald-800'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Fechas — calendario optimizado del sitio (DatePickerPopover) */}
            <button
              ref={dateTriggerRef}
              type="button"
              onClick={() => setDatePickerOpen(true)}
              className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-gray-300 px-4 py-3 text-left transition-colors hover:border-gray-400"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <CalendarDays className="h-4.5 w-4.5 shrink-0 text-gray-500" strokeWidth={1.75} />
                {arrival && departure ? (
                  <span className="truncate text-sm font-medium text-gray-900">
                    {formatRangoCorto(arrival, departure)}
                    <span className="ml-1.5 font-normal text-gray-500">
                      · {nights} {nights === 1 ? 'noche' : 'noches'}
                    </span>
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">Elige tus fechas</span>
                )}
              </span>
              <span className="shrink-0 text-xs font-semibold text-emerald-700">
                {arrival ? 'Cambiar' : 'Abrir calendario'}
              </span>
            </button>

            <DatePickerPopover
              isOpen={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              onConfirm={() => setDatePickerOpen(false)}
              arrival={arrival}
              nights={nights}
              onArrivalChange={setArrival}
              onNightsChange={setNights}
              minNights={2}
              getAnchorEl={() => dateTriggerRef.current}
            />

            {/* Huéspedes — mismo selector del buscador (adultos + niños con
                edades). El contador plano anterior no dejaba declarar niños y
                el staff cotizaba a ciegas. */}
            <button
              ref={guestTriggerRef}
              type="button"
              onClick={() => setGuestPickerOpen(true)}
              className="mt-3 flex w-full items-center justify-between gap-3 rounded-xl border border-gray-300 px-4 py-3 text-left transition-colors hover:border-gray-400"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <Users className="h-4.5 w-4.5 shrink-0 text-gray-500" strokeWidth={1.75} />
                <span className="truncate text-sm font-medium text-gray-900">{guestsLabel}</span>
              </span>
              <span className="shrink-0 text-xs font-semibold text-emerald-700">Cambiar</span>
            </button>

            <GuestPickerPopover
              isOpen={guestPickerOpen}
              onClose={() => setGuestPickerOpen(false)}
              onConfirm={() => setGuestPickerOpen(false)}
              numAdults={numAdults}
              onNumAdultsChange={setNumAdults}
              onChildrenChange={setChildrenAges}
              initialChildren={childrenAges}
              getAnchorEl={() => guestTriggerRef.current}
            />
          </>
        )}

        <button
          type="button"
          disabled={!canContinue}
          onClick={handleContinue}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-wsp-brand px-5 py-3 font-semibold text-wsp-ink hover:bg-wsp-brand-dark hover:-translate-y-px transition-all disabled:opacity-40 disabled:hover:translate-y-0"
        >
          {isResultsMode ? (
            'Ver disponibilidad'
          ) : (
            <>
              <WhatsAppGlyph size={18} />
              {isUrgentPick
                ? tr('Solicitar salida urgente', 'Request urgent departure')
                : tr('Continuar a WhatsApp', 'Continue to WhatsApp')}
            </>
          )}
        </button>
        {/* Decisión founder 2026-08-11 (A4): «Preguntar sin fecha» visible al
            mismo nivel que el CTA con fecha, medido 14 días por sesión. Outline
            del sistema WhatsApp (wsp-deep monótono), sin cambiar identidad. */}
        <button
          type="button"
          onClick={handleBypass}
          className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl border border-wsp-deep/40 bg-white px-5 py-3 font-semibold text-wsp-deep transition-all duration-200 ease-out hover:bg-wsp-deep/5 hover:border-wsp-deep/60 active:bg-wsp-deep/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wsp-deep"
        >
          <WhatsAppGlyph size={18} />
          {variant === 'tour' ? tr('Preguntar sin fecha', 'Ask without a date') : 'Preguntar sin fechas'}
        </button>
        <p className="mt-3 text-center text-xs text-gray-400">
          {tr('Respondemos en minutos en horario de atención.', 'We reply during opening hours.')}
        </p>
      </div>
    </div>,
    portalRoot
  );
}
